import api from './api';

export type TipoConta = 'ASSET' | 'LIABILITY' | 'REVENUE' | 'EXPENSE';
export type ClasseFundos = 'SEGREGATED' | 'OWN';
export type DonoConta = 'PLATFORM' | 'PARTICIPANT' | 'PROMOTER' | 'VENDOR' | 'PSP' | 'AGENT';

export interface ContaLedger {
  id: string;
  tipo: TipoConta;
  fundClass: ClasseFundos;
  donoTipo: DonoConta;
  donoId: string | null;
  moeda: string;
  debito: number;
  credito: number;
  /** Saldo no sentido natural da conta — activos a débito, passivos a crédito. */
  saldo: number;
  linhas: number;
  criadaEm: string;
  emUso: boolean;
}

export interface LinhaLancamento {
  conta: string;
  direccao: 'DEBIT' | 'CREDIT';
  montante: number;
  refTipo: string | null;
  refId: string | null;
}

export interface Lancamento {
  id: string;
  evento: string;
  chaveIdempotencia: string;
  debito: number;
  credito: number;
  descricao: string | null;
  criadoEm: string;
  linhas: LinhaLancamento[];
}

export interface Verificacao {
  check: string;
  ok: boolean;
  detail?: string;
}

export interface NovaConta {
  id: string;
  accountType: TipoConta;
  fundClass: ClasseFundos;
  ownerType: DonoConta;
  ownerId?: string;
}

/**
 * Ledger de dupla entrada.
 *
 * O plano de contas só existia dentro de migrações e os lançamentos só se viam
 * por SQL.
 */
export const ledgerAdminService = {
  async contas() {
    const { data } = await api.get('/admin/ledger/contas');
    return {
      contas: data.data as ContaLedger[],
      totais: data.totais as { contas: number; debito: number; credito: number; equilibrado: boolean },
      opcoes: data.opcoes as { tipos: TipoConta[]; classes: ClasseFundos[]; donos: DonoConta[] },
    };
  },

  async criarConta(c: NovaConta): Promise<{ message: string; aviso?: string }> {
    const { data } = await api.post('/admin/ledger/contas', c);
    return { message: data.message, aviso: data.aviso };
  },

  async actualizarConta(id: string, patch: Partial<NovaConta>): Promise<{ message: string; aviso?: string }> {
    const { data } = await api.patch(`/admin/ledger/contas/${id}`, patch);
    return { message: data.message, aviso: data.aviso };
  },

  async lancamentos(f: { from?: string; to?: string; conta?: string; evento?: string; page?: number; limit?: number } = {}) {
    const { data } = await api.get('/admin/ledger/lancamentos', { params: f });
    return {
      linhas: data.data as Lancamento[],
      totais: data.totais as { lancamentos: number },
      eventos: data.eventos as string[],
    };
  },

  async integridade() {
    const { data } = await api.get('/admin/ledger/integridade');
    return data.data as {
      ok: boolean;
      verificacoes: Verificacao[];
      coberturaSegregadaCVE: number;
      resumo: string;
    };
  },
};

export default ledgerAdminService;
